<script setup lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'

import { useAuthStore } from '@/stores/auth.store'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

const router = useRouter()
const authStore = useAuthStore()

const email = ref('')
const password = ref('')
const errorMessage = ref<string | null>(null)
const isLoading = ref(false)

async function handleLogin() {
  if (!email.value || !password.value) {
    errorMessage.value = 'Email and password are required'
    return
  }

  errorMessage.value = null
  isLoading.value = true

  try {
    await authStore.login(email.value, password.value)
    router.push({ name: 'home' })
  } catch {
    errorMessage.value = 'Invalid email or password. Please try again.'
  } finally {
    isLoading.value = false
  }
}
</script>

<template>
  <main class="flex items-center justify-center min-h-screen bg-gray-50 p-8">
    <Card class="w-full max-w-sm" aria-labelledby="login-heading">
      <CardHeader>
        <CardTitle id="login-heading" class="text-2xl text-center"> Welcome back! </CardTitle>
      </CardHeader>
      <CardContent>
        <form id="login-form" @submit.prevent="handleLogin">
          <div class="grid gap-4">
            <div class="grid gap-2">
              <Label for="email">Email</Label>
              <Input
                id="email"
                v-model="email"
                type="email"
                placeholder="name@example.com"
                required
                autofocus
                aria-required="true"
              />
            </div>
            <div class="grid gap-2">
              <Label for="password">Password</Label>
              <Input
                id="password"
                v-model="password"
                type="password"
                autocomplete="off"
                required
                aria-required="true"
              />
            </div>
          </div>
        </form>
        <p v-if="errorMessage" class="text-red-500 text-sm text-center">{{ errorMessage }}</p>
      </CardContent>
      <CardFooter>
        <Button type="submit" form="login-form" class="w-full" :disabled="isLoading">
          {{ isLoading ? 'Loading...' : 'Sign in' }}
        </Button>
      </CardFooter>
    </Card>
  </main>
</template>
